/**
 * What a contact *is*, and what is worth saying about it on a given screen.
 *
 * A contact carries three independent flags — it can be a client, a supplier and
 * a prospect at once — so "which type is this?" has no single answer and the
 * badges have to show the combination.
 *
 * The screen around them already answers part of the question: in the Clients
 * list every record is a client, and repeating it on each row and on the fiche
 * says nothing. What earns a badge there is the *other* role — that this client
 * is also a supplier, or still an open prospect. That is `exclude`, and it comes
 * from the route the screen is on, never from a hard-coded page name: the same
 * List and Preview components serve /contacts, /customers, /suppliers and
 * /prospects.
 */

/**
 * Each role carries its own colour rather than one of Bulma's semantic ones.
 * `is-info` and `is-success` name meanings these do not have — a supplier is not
 * a success — and borrowing them meant the three roles moved whenever those
 * meanings were restyled. The palette lives in `global.scss` as
 * `--elx-role-*`, so dark mode moves it.
 *
 * @typedef {{slug: string, flag: string, label: () => string, type: string}} ContactRole
 */

/**
 * The three roles, in the order they read on screen, with the colours the Type
 * column has always used so the same record looks the same wherever it appears.
 *
 * `label` stays a function: these are module-level constants, and i18n is not
 * ready at import time.
 *
 * @type {ContactRole[]}
 */
export const CONTACT_ROLES = [
    { slug: 'customer', flag: 'is_customer', label: () => i18n.t('Customer'), type: 'is-role-customer' },
    { slug: 'supplier', flag: 'is_supplier', label: () => i18n.t('Supplier'), type: 'is-role-supplier' },
    { slug: 'prospect', flag: 'is_prospect', label: () => i18n.t('Prospect'), type: 'is-role-prospect' }
];

/**
 * The roles a screen should badge for a contact.
 *
 * @param {Object} contact         The contact row, carrying the `is_*` flags.
 * @param {?string} exclude        Role slug the surrounding screen already states.
 * @returns {ContactRole[]}
 */
export function visibleRoles(contact, exclude = null) {
    return CONTACT_ROLES.filter(role => Boolean(contact?.[role.flag]) && role.slug !== exclude);
}

/**
 * The contact type a route belongs to — the scope of the screen being shown.
 *
 * Matches both the list route (`customers-list`) and every route generated under
 * a type's prefix (`customer-preview`, `customer-overview`, `customer-tasks`…),
 * so a fiche knows its scope as well as a list does. `/contacts` and the global
 * search bar match nothing: they state no role, so nothing is excluded there.
 *
 * @param {Array<{slug: string, listRoute: string, routePrefix: string}>} types
 * @param {?string} routeName
 * @returns {?Object} The matching type, or null outside any typed section.
 */
export function contactTypeForRoute(types, routeName) {
    if (!routeName) return null;

    return (
        (types || []).find(type => routeName === type.listRoute || routeName.startsWith(type.routePrefix + '-')) || null
    );
}

/**
 * The ListColumnCatalog object type of a typed section's list — the key its
 * column manager and its per-user preferences are stored under.
 *
 * The four sections share one List and one fiche, so both ends have to name the
 * same list from the route alone: a user who asked the Clients list to open
 * records in edit mode asked it of the client fiche, not of every contact.
 *
 * @param {?string} slug A contact role slug, or null under /contacts
 * @returns {string}
 */
export function listObjectTypeForRole(slug) {
    return { customer: 'customers', supplier: 'suppliers', prospect: 'prospects' }[slug] || 'contacts';
}

/**
 * What the contact type field is called in a typed section: « Type de client »
 * under Clients. The create form and the fiche both name it after the list they
 * are opened from; Contacts keeps « Type de contact ».
 *
 * @param {?string} slug A contact role slug, or null under /contacts
 * @returns {string} The translated label.
 */
export function contactTypeFieldLabel(slug) {
    const key = { customer: 'Customer type', supplier: 'Supplier type', prospect: 'Prospect type' }[slug];

    return i18n.t(key || 'Contact type');
}

/**
 * Whether the contact holds any business role at all.
 *
 * Portal access is what a role is *for* — there is nothing for a contact who is
 * neither prospect, client nor supplier to log into — so the switch stays shut
 * until one is taken. The server says the same thing from its side:
 * `ContactController::store()` turns portal access on by itself the moment a
 * role is set. This is only the screen agreeing with it, and it lives here so
 * the fiche and the create form cannot drift apart on the answer.
 *
 * @param {{is_prospect?: *, is_customer?: *, is_supplier?: *}} contact
 * @returns {boolean}
 */
export function hasAnyRole(contact) {
    return CONTACT_ROLES.some(role => Boolean(contact?.[role.flag]));
}
